import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = 'https://startawebb.se/og/startawebb-og.jpg?v=20261024',
  ogType = 'website',
  structuredData = null,
  keywords = '',
  language = 'sv',
  locale = 'sv_SE',
  direction = 'ltr',
  noindex = false,
  alternateUrls = [],
  ogAlternateLocales = [],
}) => {
  const isArabic = language.toLowerCase().startsWith('ar');

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: isArabic ? 'StartaWebb بالعربية' : 'StartaWebb',
    description: isArabic
      ? 'تصميم مواقع سويدية لأصحاب الشركات الناطقين بالعربية، مع مساعدة في المحتوى والتواصل والنشر.'
      : 'Hemsidor för små serviceföretag i Kristinehamn och Värmland med svensk textbearbetning, mobilanpassning och kontaktformulär',
    url: isArabic ? 'https://startawebb.se/ar' : 'https://startawebb.se',
    telephone: '+46734877119',
    email: 'info@startawebb.se',
    inLanguage: isArabic ? 'ar-SE' : 'sv-SE',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'SE',
      addressRegion: 'Värmland',
      addressLocality: 'Kristinehamn',
    },
    areaServed: { '@type': 'Country', name: 'Sweden' },
    priceRange: isArabic
      ? 'سعر إرشادي نحو 4900 كرون لموقع شركة بسيط'
      : 'Riktpris 4900 SEK. Totalpris och eventuell moms anges i skriftlig offert.',
    serviceType: isArabic
      ? ['مواقع سويدية للشركات', 'مواقع شركات التنظيف والنقل', 'تحسين مواقع الشركات الموجودة']
      : ['Företagshemsidor', 'Förbättring av befintliga hemsidor', 'Webbutveckling för designers och byråer'],
  };

  const normalizedStructuredDataArray = Array.isArray(structuredData)
    ? [defaultStructuredData, ...structuredData]
    : structuredData
      ? [defaultStructuredData, structuredData]
      : [defaultStructuredData];

  return (
    <Helmet htmlAttributes={{ lang: language, dir: direction }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {alternateUrls.map((item) => (
        <link key={`${item.hrefLang}-${item.href}`} rel="alternate" hrefLang={item.hrefLang} href={item.href} />
      ))}

      <meta property="og:locale" content={locale} />
      {ogAlternateLocales.map((alternateLocale) => (
        <meta key={alternateLocale} property="og:locale:alternate" content={alternateLocale} />
      ))}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="StartaWebb" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <meta name="author" content="StartaWebb" />
      <meta name="language" content={language} />

      {normalizedStructuredDataArray.map((data, index) => (
        <script key={`ld-${index}`} type="application/ld+json">{JSON.stringify(data)}</script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
